/**
 * 미션 상태 지도 — 첫 미션을 **재태그**한 아이에게 "어디를 했고 어디가 남았는지" 보여준다.
 *
 * 디자인: Figma `2483:75533`
 * 기획: docs/2026-08-05-quest-first-mission-status-map-dev-steps.md
 *
 * 🔴 마커 좌표는 **지도 이미지 기준 0~1** 이다. 그래서 뷰포트가 아니라 **이미지 상자**에
 *    붙여야 한다 — `.msm-stage` 가 `<img>` 를 감싸 이미지 렌더 크기 그대로 줄어들고,
 *    마커는 그 안에서 `%` 로 놓인다. 이 구조 덕에 해상도·화면비가 달라도 좌표가 어긋나지
 *    않는다(안내판에서 vw/vh 로 계산하다 겪은 어긋남을 구조로 차단한다).
 *
 * 🔴 서버가 `spots` 를 비워 보내면(지도 미등록·좌표 미입력) **렌더하지 않는다** —
 *    호출부가 기존 안내 메시지로 폴백해야 아이가 빈 화면을 보지 않는다.
 */
import React from 'react';
import './MissionStatusMap.css';

/** 같은 지점으로 볼 좌표 정밀도 — 0.001 ≈ 1476px 지도에서 1.5px. */
const CLUSTER_PRECISION = 3;
/** 군집 반지름(디자인 px). 핀 폭이 56 이라 32 면 서로 겹치면서도 상태가 다 보인다. */
const CLUSTER_RADIUS = 32;

/**
 * 같은 자리에 여러 미션이 있으면 **원형으로 겹쳐** 배치한다(사용자 요청 2026-08-05).
 *
 * 🔴 겹친 핀을 그대로 두면 위의 하나만 보여 **아래 미션의 완료/미완료 상태가 사라진다.**
 *    12시 방향부터 등간격으로 돌려 모두 보이게 한다.
 * 반환은 원본 + `dx`,`dy`(디자인 px 오프셋). 단독 스팟은 0,0 이라 기존 위치 그대로다.
 */
export function clusterSpots(spots, { radius = CLUSTER_RADIUS } = {}) {
  const groups = new Map();
  (spots || []).forEach((s) => {
    const key = `${Number(s.x).toFixed(CLUSTER_PRECISION)}:${Number(s.y).toFixed(CLUSTER_PRECISION)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(s);
  });
  const out = [];
  groups.forEach((group) => {
    if (group.length === 1) {
      out.push({ ...group[0], dx: 0, dy: 0 });
      return;
    }
    const step = (Math.PI * 2) / group.length;
    group.forEach((s, i) => {
      const angle = -Math.PI / 2 + i * step; // 12시 방향에서 시계 방향으로
      out.push({
        ...s,
        dx: Math.round(Math.cos(angle) * radius),
        dy: Math.round(Math.sin(angle) * radius),
      });
    });
  });
  return out;
}

/** 지도를 띄울 수 있는 응답인가 — 폴백 판정의 **단일 지점**. */
export function canRenderStatusMap(data) {
  return !!(data && data.map && data.map.image_url
    && Array.isArray(data.spots) && data.spots.length > 0);
}

function Pin({ done }) {
  return (
    <>
      <span className={`msm-pin-card${done ? ' msm-pin-card--done' : ''}`}>
        {done ? (
          <svg className="msm-pin-glyph" viewBox="0 0 20 14" aria-hidden="true">
            <path d="M1.8 6.6l5.2 5.2L18.2 1.6" fill="none" stroke="#fff"
                  strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg className="msm-pin-glyph" viewBox="0 0 12 28" aria-hidden="true">
            <path d="M6 0.5v18" fill="none" stroke="#fff" strokeWidth="4.4" strokeLinecap="round" />
            <circle cx="6" cy="25.2" r="2.4" fill="#fff" />
          </svg>
        )}
      </span>
      <span className="msm-pin-tail" aria-hidden="true" />
    </>
  );
}

/**
 * 미션 상태 지도 — Figma `3379:44594`(시작 안내) / `3379:44803`(진행 중).
 *
 * 🔴 두 프레임은 **지도·배너 좌표가 같고 배너 2행 문구만 다르다.** 그래서 화면을 둘로 만들지
 *    않고 문구를 prop 으로 받는다(2026-08-19 개편). 신규 컴포넌트를 만들면 좌표계가 두 벌이 된다.
 *
 * 🔴 `agentTitle` 은 **완성된 문자열**을 받는다. `nickname` 을 받아 여기서 조립하면 서버
 *    중립값(`'친구'`) 축약 규칙이 두 벌이 되거나 `QuestTagPage` 를 import 해 **순환 의존**이
 *    된다. 조립은 `buildFirstMissionAgentTitle` 단일 출처가 맡고 여기는 표시만 한다.
 *
 * @param {object} props
 * @param {{map:{image_url:string}, done:number, total:number,
 *          spots:Array<{spot_id:number,name:string,done:boolean,x:number,y:number}>}} props.data
 * @param {string} [props.agentTitle] 배너 1행 — 예: `견습 주키퍼 별이 요원`
 * @param {string} [props.bannerText] 배너 2행 — 분기별 문구
 */
export default function MissionStatusMap({ data, agentTitle = '', bannerText = '' }) {
  if (!canRenderStatusMap(data)) return null;
  const { map, spots } = data;

  return (
    <div className="msm" data-testid="mission-status-map">
      {/* 🔴 프레임과 스테이지를 분리한다. Figma 는 지도를 1320x1008 영역에 놓지만, 실제 지도는
          지점마다 종횡비가 다르다(실측 1920x835 / 4200x2980). `.msm-stage` 가 `<img>` 에 **딱
          맞아야** 마커의 `%` 좌표가 이미지 위 같은 지점을 가리킨다(contain 규약, 검증 E4).
          프레임이 위치·최대크기를 맡고, 스테이지는 이미지 크기를 그대로 따른다. */}
      <div className="msm-frame">
        <div className="msm-stage">
          <img className="msm-map" src={map.image_url} alt="" />
          {clusterSpots(spots).map((s) => (
            <span
              key={s.spot_id}
              className={`msm-pin${s.done ? ' msm-pin--done' : ''}`}
              data-testid={`msm-pin-${s.spot_id}`}
              data-done={s.done ? '1' : '0'}
              data-dx={s.dx}
              data-dy={s.dy}
              style={{
                left: `${s.x * 100}%`,
                top: `${s.y * 100}%`,
                // 군집 오프셋은 **디자인 px 배수**로 넘기고 환산은 CSS 가 한다(--msm-u 단일 소유).
                '--msm-dx': s.dx,
                '--msm-dy': s.dy,
              }}
              title={s.name}
            >
              <Pin done={s.done} />
            </span>
          ))}
        </div>
      </div>

      {/* 배너 — 2026-08-19 개편. 옛 진행도 알약("미션을 얼마나 완료했나요? N/M")을 대체한다.
          남겨 두면 머리가 둘이 되어 아이가 어디를 봐야 할지 모른다(작업로그 공시).
          지도 **위에 겹치는** 오버레이다(Figma). 2026-08-05 에 "알약이 지도를 가린다"는 이유로
          쌓기 배치로 바꿨던 이력이 있으므로, 가림 여부는 배포 후 실측으로 확인한다. */}
      <div className="msm-banner" data-testid="msm-banner">
        <span className="msm-banner-icon" aria-hidden="true" />
        <p className="msm-banner-title">{agentTitle}</p>
        <p className="msm-banner-text">{bannerText}</p>
      </div>
    </div>
  );
}
